import { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useGeolocation } from '@/hooks/useGeolocation';
import { submitLead, handleLeadResponse } from '@/utils/apiService';

const ContactUs = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const { geoData } = useGeolocation();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+1',
    phone: '',
    message: ''
  });

  // Auto-populate country code from geolocation
  useEffect(() => {
    if (geoData?.data?.countryCode) {
      const dialCode = getDialCodeFromCountry(geoData.data.countryCode);
      if (dialCode) {
        setFormData(prev => ({ ...prev, country_code: dialCode }));
      }
    }
  }, [geoData]);

  const getDialCodeFromCountry = (countryCode) => {
    const countryDialCodes = {
      'US': '+1', 'CA': '+1', 'GB': '+44', 'IN': '+91', 'AU': '+61',
      'DE': '+49', 'FR': '+33', 'JP': '+81', 'CN': '+86', 'KR': '+82',
      'BR': '+55', 'MX': '+52', 'IT': '+39', 'ES': '+34', 'NL': '+31',
      'SE': '+46', 'CH': '+41', 'SG': '+65', 'AE': '+971', 'ZA': '+27',
      'NZ': '+64', 'PK': '+92'
    };
    return countryDialCodes[countryCode] || null;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const numbersOnly = value.replace(/\D/g, '');
    const limitedValue = numbersOnly.slice(0, 10);
    
    setFormData(prev => ({ ...prev, phone: limitedValue }));
    
    if (limitedValue.length === 10) {
      setPhoneError('');
    } else if (limitedValue.length > 0) {
      setPhoneError('Phone number must be 10 digits');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (phoneError || formData.phone.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await submitLead(formData, geoData);
      const success = handleLeadResponse(response, setSubmitError);
      
      if (success) {
        return;
      }
    } catch (error) {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us - Trademark Scribe"
        description="Get in touch with our trademark experts. Free consultation available. Contact us today for professional trademark registration services."
      />
      <Header />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-4">Get in Touch</h1>
              <p className="text-xl text-gray-300">
                Have questions? Our trademark experts are here to help you protect your brand.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-bold mb-4">Contact Information</h2>
                  <p className="text-gray-600 mb-8">
                    Reach out to us and we&apos;ll respond within 24 hours.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Email</h3>
                      <p className="text-gray-600">info@trademarkscribe.com</p>
                      <p className="text-sm text-gray-500">24/7 Support Available</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Phone</h3>
                      <p className="text-gray-600">+1 (888) 608-2752</p>
                      <p className="text-sm text-gray-500">Mon-Fri 9AM - 6PM EST</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Office</h3>
                      <p className="text-gray-600">7500 Monterey Rd</p>
                      <p className="text-gray-600">Gilroy, CA 95020, United States</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Business Hours</h3>
                      <p className="text-gray-600">Monday - Friday: 9AM - 6PM</p>
                      <p className="text-gray-600">Saturday: 10AM - 4PM</p>
                      <p className="text-gray-600">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <h2 className="text-2xl font-bold mb-6">Send us a Message</h2>
                
                {submitError && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="flex space-x-2 mt-1">
                      <Select
                        value={formData.country_code}
                        onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+1">+1</SelectItem>
                          <SelectItem value="+44">+44</SelectItem>
                          <SelectItem value="+91">+91</SelectItem>
                          <SelectItem value="+61">+61</SelectItem>
                          <SelectItem value="+86">+86</SelectItem>
                          <SelectItem value="+81">+81</SelectItem>
                          <SelectItem value="+49">+49</SelectItem>
                          <SelectItem value="+33">+33</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex-1">
                        <Input
                          id="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          placeholder="1234567890"
                          maxLength={10}
                          className={phoneError ? 'border-red-500' : ''}
                        />
                        {phoneError && (
                          <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your trademark needs..."
                      className="mt-1"
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting || phoneError}
                    className="w-full bg-gradient-to-r from-red-600 to-orange-500 text-white py-3 rounded-full font-semibold hover:shadow-lg disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    🔒 Your information is secure and confidential
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default ContactUs;